// Order feedback Overview page copy. Source: the pinned prototype
// (order-feedback-landing-page/v2 v58, blueprint 8.4), transcribed verbatim
// including punctuation and dashes. Where prose specifying a state disagreed
// with the prototype, the prototype's copy is what's recorded here.
//
// Labels styled as all-caps in the design are stored in natural case and
// uppercased in CSS: the caps are presentation, and baking them in would
// impose English casing on every other locale.
export default {
  // Natural case; the eyebrow uppercases via CSS text-transform, so screen
  // readers get the words rather than letters spelled out.
  eyebrow: 'Operations · Order feedback',
  turn_on_live_updates_cta: 'Turn on live updates',
  // Alt text for the RecoveryAI logo mark shown beside the promo heading.
  promo_logo_alt: 'RecoveryAI',
  // Alt text for the two-phone screenshot in the promo band, taken from the
  // prototype. It describes what the shot demonstrates rather than what it
  // literally depicts, which is the claim the bullets beside it back up.
  promo_conversation_alt: 'RecoveryAI replies to guests for you',
  // Accessible name for the Thanx app icon in the setup card's tile. The tile
  // carries the icon as a background image rather than an <img>, so this is
  // the only name it has.
  thanx_logo_alt: 'Thanx',
  // Alt text for the product recording filling the hero panel in every
  // RecoveryAI state. Taken from the prototype, which gives the same shot the
  // same description.
  rai_hero_alt: 'RecoveryAI resolving a guest issue',
  // Shown above the hero wherever Thanx itself is not collecting. Only the
  // opening clause of line 2 names a connected tool, so just that clause is
  // dropped when there isn't one; the rest of line 2 (and lines 1 and 3) are
  // true either way.
  feedback_setup_heading: 'Your feedback setup',
  // The design sets the promo heading as the RecoveryAI wordmark followed by
  // the copy, so each state's `promo.heading` is only the trailing half of the
  // sentence. The wordmark supplies the subject visually and `promo_logo_alt`
  // supplies it to a screen reader; this key is what the two must agree on,
  // and is still the name to use anywhere the mark itself doesn't fit.
  product_name: 'RecoveryAI',
  // Appended to the accessible name of any control that opens a new tab, so
  // a screen reader announces the jump the ↗ only shows sighted users. Same
  // key and wording as promoCodes/overview.ts, kept local rather than
  // imported across the scene boundary.
  opens_in_new_window: '(opens in new window)',
  comments_off_banner: {
    line_1: 'Live Updates are active and collecting order ratings',
    // Line 2 is assembled from up to three pieces rather than held as one
    // string, because its link has to be a real element: a whole-sentence
    // string would have to carry raw markup, which cannot host a link. Same
    // shape as every other in-prose link in the app (see OrderingManagement's
    // ErrorCategoryCard).
    //
    // `line_2_provider` is the OPTIONAL opener — it names the connected tool,
    // so it is dropped when no provider is connected. `line_2` and its
    // `line_2_link` tail always render, with or without a provider, since the
    // ratings report is reachable either way.
    line_2_provider: 'Written comments come in through %{provider}.',
    line_2: 'View aggregate order ratings at',
    // No arrow glyph in the copy. AI-1342 briefly spelled this `Order
    // Ratings →`, on the grounds that ↗ in this app is reserved for a real
    // new-window open plus an `opens_in_new_window` label, which an in-tab
    // router link could not honour. That reasoning still holds — the link
    // now satisfies it instead of dodging it: it genuinely opens a new tab,
    // carries the ExternalLink ↗ icon, and appends the label below. The
    // arrow is therefore composed as an icon beside the control, not stated
    // in the string.
    line_2_link: 'Order Ratings',
    line_3: 'You are not collecting feedback or comments via Thanx.',
  },
  states: {
    new: {
      hero: {
        headline: 'When an order goes wrong, make it right and win guests back',
        lede: 'After a negative order rating, guests can tell you what happened. You decide how to respond: yourself in a couple of clicks, or automatically with Recovery AI.',
        cta_primary: 'Turn on order feedback',
        cta_secondary: 'Explore RecoveryAI',
      },
      feedback_setup: {
        badge: 'Not yet setup',
        title: 'You’re not collecting feedback from guests',
        label: 'What this means for you',
        body: 'Guests who have a poor experience have no way to tell you what went wrong, so issues go unheard and unresolved. Turn on order feedback to hear directly from unhappy guests, respond with full order context, and make it right before they move on.',
        cta: 'Turn on feedback',
      },
      promo: {
        heading: 'responds for you',
        bullet_1: 'Replies to every guest personally, instantly, 24/7',
        bullet_2: 'Resolves with the make-good rules you teach it once',
        badge: 'Free to Activate',
        cta: 'Start a pilot →',
      },
      how_it_works: {
        heading: 'How it works',
        // Split around its link: `intro_link` is the word the design turns
        // into a control onto the Settings tab, and `intro_suffix` carries
        // the sentence-final punctuation that follows it, so a locale can
        // move or change that mark rather than inherit a period from JSX.
        intro: 'Three moments, start to finish. Every step is configurable in',
        intro_link: 'settings',
        intro_suffix: '.',
        step_1_title: "Guest's order has issues",
        step_1_body:
          'Thumbs up or down, right inside your app or web ordering. Ratings feed the Order ratings report either way.',
        step_2_title: 'Unhappy guests tell you why',
        step_2_body:
          'After a thumbs-down, guests add a short comment and immediately see your acknowledgment message, in your voice.',
        step_3_title: 'You (or Recovery AI) respond',
        step_3_body:
          'Reply and resolve from your inbox in two clicks – or let Recovery AI handle the conversation and the make-good for you.',
      },
    },
    thanx: {
      hero: {
        headline: 'When an order goes wrong, make it right and win guests back',
        lede: 'After a negative order rating, guests can tell you what happened. You decide how to respond: yourself in a couple of clicks, or automatically with Recovery AI.',
        cta_primary: 'View all feedback',
        cta_secondary: 'Explore RecoveryAI',
      },
      feedback_setup: {
        badge: 'Your current setup',
        title: 'Collecting feedback with Thanx',
        label: 'What this means for you',
        body: 'Every thumbs-down lands in your inbox. Reply and resolve in two clicks, and attach a reward when it’s warranted.',
        cta: 'View all feedback →',
      },
      promo: {
        heading: 'responds for you',
        bullet_1: 'Replies to every guest personally, instantly, 24/7',
        bullet_2: 'Resolves with the make-good rules you teach it once',
        badge: 'Free to Activate',
        cta: 'Start a pilot →',
      },
      how_it_works: {
        heading: 'How it works',
        // Split around its link: `intro_link` is the word the design turns
        // into a control onto the Settings tab, and `intro_suffix` carries
        // the sentence-final punctuation that follows it, so a locale can
        // move or change that mark rather than inherit a period from JSX.
        intro: 'Three moments, start to finish. Every step is configurable in',
        intro_link: 'settings',
        intro_suffix: '.',
        step_1_title: "Guest's order has issues",
        step_1_body:
          'Thumbs up or down, right inside your app or web ordering. Ratings feed the Order ratings report either way.',
        step_2_title: 'Unhappy guests tell you why',
        step_2_body:
          'After a thumbs-down, guests add a short comment and immediately see your acknowledgment message, in your voice.',
        step_3_title: 'You (or Recovery AI) respond',
        step_3_body:
          'Reply and resolve from your inbox in two clicks – or let Recovery AI handle the conversation and the make-good for you.',
      },
    },
    // No `hero` block: this state renders no hero marketing content at all.
    thirdparty: {
      feedback_setup: {
        status: 'Collecting feedback with %{provider}',
        sub_copy:
          '%{provider} keeps the full guest picture. RecoveryAI catches order issues in real time and responds before the guest is gone.',
      },
      promo: {
        heading: 'works seamlessly side by side with your feedback tools',
        bullet_1:
          '%{provider} keeps your full guest-satisfaction picture — in-store, staff, and overall.',
        bullet_2:
          'RecoveryAI catches order issues the moment they happen and resolves them automatically, before the guest is gone.',
        bullet_3:
          'Nothing changes about your %{provider} setup — RecoveryAI runs alongside it, so you get the benefits of both without extra work.',
        badge: 'Free to Activate',
        cta: 'Start a pilot →',
      },
    },
    rai_thanx: {
      hero: {
        // %{enabled}/%{total} are supplied by the caller once a real
        // location-count signal exists — the resolver only decides whether
        // the badge should render, not the count.
        badge: '%{enabled} of %{total} locations on',
        headline: 'RecoveryAI is handling your digital order feedback',
        // KNOWN WART, carried over deliberately: this state is also reachable
        // via the not-collecting/no-third-party/partial-RAI default, where the
        // Feedback tab this line points to is hidden. Not rewritten — flagged
        // for design instead.
        lede: 'Head to the <strong>Feedback</strong> tab to handle the cases RecoveryAI escalated.',
        // %{remaining} is total minus enabled, not the enabled count again —
        // see the resolver module comment on this exact mixup.
        nudge:
          'Working well? Turning RecoveryAI on at your other %{remaining} locations takes just a couple of clicks.',
        cta_primary: 'View escalations',
        cta_secondary: 'Manage RecoveryAI',
      },
    },
    rai_all: {
      hero: {
        headline: 'RecoveryAI is handling your digital order feedback',
        // Rewritten to drop the "head to the Feedback tab" line entirely —
        // full RAI coverage now hides that tab (see resolveOverviewState's
        // feedbackTabVisible rule), so pointing at it here was never right.
        lede: 'Every order issue is intercepted and resolved automatically. Review the cases RecoveryAI escalates and manage its messaging in RecoveryAI — there’s no separate feedback inbox to work here.',
        // Single CTA — "View escalations" was removed: at full RAI coverage
        // there's no separate Feedback tab to view escalations in.
        cta_primary: 'Manage RecoveryAI',
      },
    },
    rai_thirdparty: {
      hero: {
        headline: 'RecoveryAI is catching order issues in real time',
        lede: '%{provider} keeps the full guest feedback picture. RecoveryAI still intercepts order issues automatically and responds before the guest is gone — you only step in for the cases it escalates.',
        cta_primary: 'Manage RecoveryAI',
      },
      feedback_setup: {
        status: 'Collecting feedback with %{provider}',
        sub_copy:
          '%{provider} keeps the full guest picture of every visit — in-store, staff and overall satisfaction.',
      },
      promo: {
        heading: 'works seamlessly side by side with your feedback tools',
        bullet_1:
          '%{provider} keeps your full guest-satisfaction picture — in-store, staff, and overall.',
        bullet_2:
          'RecoveryAI catches order issues the moment they happen and resolves them automatically, before the guest is gone.',
        bullet_3:
          'Nothing changes about your %{provider} setup — RecoveryAI runs alongside it, so you get the benefits of both without extra work.',
        badge: 'Active',
        cta: 'Manage RecoveryAI →',
      },
    },
  },
  // Page-shell chrome — the header and tab bar wrapping every state above,
  // not state-specific content.
  //
  // context:
  //   `description` is deliberately NOT state-specific. The design sets the
  //   same sentence in all six Overview states, so it belongs to the shell
  //   beside the title rather than to any state's descriptor — a per-state
  //   key would invite six copies of one string and drift between them.
  header: {
    // No thumbs up/down emoji here or on the Feedback tab — both glyphs and
    // their aria labels were dropped when the combined "Order 👍/👎" page
    // split into this page and Order ratings.
    title: 'Order feedback',
    description:
      'When a guest rates an order thumbs-down, their feedback lands here. Respond yourself, or let Recovery AI handle it.',
  },
  tabs: {
    overview: 'Overview',
    feedback: 'Feedback',
    settings: 'Settings',
    tablist_label: 'Order feedback views',
  },
  feedback_disabled: {
    title: 'Order feedback is turned off',
    message:
      'Written feedback and RecoveryAI are both off, so this rating has nowhere to open.',
  },
}
